/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import config from '@pages/SignInPage/config/signInUser.config.json';
import axios from 'axios';
import { logger as log } from '@common/logger';

import { Sttp } from '@common/sttp';
import { apiConfig } from '@pages/BuyInsurancePage/config/api.config';

const { toHeaderCase } = require('js-convert-case');

export interface ISignInUserProps {
  email?: string;
  password?: string;
  otp?: string;
  jwtToken: string;
  role?: string;
}

export interface ISignInUserMethods extends IPluginMethodMap {
  signInUser(args: ISignInUserProps): Promise<any>;
  getSignInType(args: { email: string; apiURL: string }): Promise<any>;
  getAccessToken(args: { jwtToken: string; role: string }): Promise<any>;
  sendOTP(args: { email: string }): Promise<any>;
}

export interface HeaderProps {
  headerName: string;
  headerValue: string;
}

export interface SignInProps {
  email: string;
  password?: string;
  otp?: string;
  jwtToken?: string;
  role: string;
  headers: HeaderProps;
  storeId?: string;
  nextAction?: string;
}

export class SignInUserPlugin implements IAgentPlugin {
  readonly methods: ISignInUserMethods;
  constructor() {
    this.methods = {
      signInUser: this.signInUser.bind(this),
      getSignInType: this.getSignInType.bind(this),
      sendOTP: this.sendOTP.bind(this),
      getAccessToken: this.getAccessToken.bind(this),
    };
  }

  async sendOTP(args: { email: string; apiURL: string; phoneNumber: string }) {
    // console.log(`args in sendOTP`, args);
    return await this.sendOTPMethod(args);
  }

  private async sendOTPMethod(args: { email: string; phoneNumber: string }) {
    try {
      let sttp = Sttp.withBaseUrl(apiConfig.rest.url);
      //  sttp =  sttp
      //     .withQueryParams({ page: 3 })
      //     .withHeaders({ 'X-API-Token': 123 })
      //     .withPayload({ name: 'Supercharge' })
      // const res = await sttp.post(`/your_url`);

      const res = await sttp.post(apiConfig?.sendOTPEP, {
        email: args?.email,
        phoneNumber: args?.phoneNumber,
      });
      if (res?.isError()) {
        log.error(
          `error in post status=${res.status()} statusText=${res.statusText()}`
        );
      }
      log.log('result of post=', res.data());
      return res.data();
    } catch (err: any) {
      log.error('error in post=', err?.message);
      throw err;
    }
  }

  async getSignInType(args: { email: string }) {
    return await this.signInType(args);
  }

  private async signInType(args: { email: string }) {
    try {
      let sttp = Sttp.withBaseUrl(apiConfig.rest.url);
      //  sttp =  sttp
      //     .withQueryParams({ page: 3 })
      //     .withHeaders({ 'X-API-Token': 123 })
      //     .withPayload({ name: 'Supercharge' })
      // const res = await sttp.post(`/your_url`);

      const res = await sttp.post(apiConfig?.SignInUserEP, {
        email: args?.email,
      });
      if (res?.isError()) {
        log.error(
          `error in post status=${res.status()} statusText=${res.statusText()}`
        );
      }
      log.log('result of post=', res.data());
      return res.data();
    } catch (err: any) {
      log.error('error in post=', err?.message);
      throw err;
    }
  }

  async getAccessToken(args: {
    apiURL: string;
    jwtToken: string;
    role: string;
  }) {
    return await this.accessToken(args);
  }

  private async accessToken(args: {
    apiURL: string;
    jwtToken: string;
    role: string;
  }) {
    try {
      let sttp = Sttp.withBaseUrl(apiConfig.rest.url);
      //  sttp =  sttp
      //     .withQueryParams({ page: 3 })
      //     .withHeaders({ 'X-API-Token': 123 })
      //     .withPayload({ name: 'Supercharge' })
      // const res = await sttp.post(`/your_url`);

      const res = await sttp.post(apiConfig?.accessTokenEP, {
        jwtToken: args?.jwtToken,
        role: args?.role,
      });
      if (res?.isError()) {
        log.error(
          `error in post status=${res.status()} statusText=${res.statusText()}`
        );
      }
      log.log('result of post=', res.data());
      return res.data();
    } catch (err: any) {
      log.error('error in post=', err?.message);
      throw err;
    }
  }

  async signInUser(args: SignInProps) {
    return await this.signInUserMethod(args);
  }

  private async signInUserMethod(args: SignInProps) {
    try {
      let sttp = Sttp.withBaseUrl(apiConfig.rest.url);
      //  sttp =  sttp
      //     .withQueryParams({ page: 3 })
      //     .withHeaders({ 'X-API-Token': 123 })
      //     .withPayload({ name: 'Supercharge' })
      // const res = await sttp.post(`/your_url`);

      const res = await sttp.post(apiConfig?.SignInUserEP, {
        email: args?.email,
        password: args?.password,
        otp: args?.otp,
        jwtToken: args?.jwtToken,
        role: this.getRoleFormat(args?.role),
      });
      if (res?.isError()) {
        log.error(
          `error in post status=${res.status()} statusText=${res.statusText()}`
        );
      }
      log.log('result of post=', res.data());
      if (res?.data()) {
        if (res?.data()?.userRoles) {
          res.data().userRoles = this.formatRoles(res?.data()?.userRoles);
        }
        return res?.data();
      }
    } catch (err: any) {
      log.error('error in post=', err?.message);
      throw err;
    }
  }

  private getRoleFormat(roles: any[]) {
    let _temp =
      Array.isArray(roles) && roles?.find((ele) => ele?.name === 'purchaser');

    if (_temp?.uuid) {
      return _temp?.uuid;
    } else return null;
  }

  private formatRoles(roles: any[]) {
    let _res: any = [];
    for (const role of roles) {
      _res.push({
        ...(role || {}),
        title: role?.name ? toHeaderCase(role?.name) : role?.name,
      });
    }
    return _res;
  }
}
